import React, { useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../../../firebase-config';
import history from '../history/History';
import { getDocument } from '../document-api/DocumentAPI';

type Children = {
  children: React.ReactNode;
};

/**
 * A shorthand method to get the current user.
 * WARNING: This method isn't initialize tolerant. This means it should be used in
 * cases when you know the user auth data is already loaded,
 * or use an observer (isLoggedInObserver) instead if it may be
 * a race case.
 *
 * Its primary usage is location where you know the user has already been defined.
 */
export const getUser = () => auth.currentUser;

/**
 * A wrapper for the onAuthStateChanged observer for firebase.
 * Returns the user auth data inside the provided state hook.
 * @param setUser a use state hook for holding the user results.
 */
export const isLoggedInObserver = (setUser: (arg0: any) => void) => (
  onAuthStateChanged(auth, (user) => {
    setUser(user);
    if (!user) {
      history.push('/login');
    }
  }));

/**
 * Defines the UserContext which will handle the 'User' object.
 */
const UserContext = React.createContext<User | null>(null);
const AdminContext = React.createContext<boolean>(false);
const RolesContext = React.createContext<Roles[]>([]);

/**
 * A facade for the user to use the user context without the setup
 */
export const useUser = () => useContext(UserContext);
export const useAdmin = () => useContext(AdminContext);
export const useRoles = () => useContext(RolesContext);

// Array of valid roles
export const validRoles = ['admin', 'superadmin', 'general', 'vendor-statements'] as const;

// Type of valid roles derived from the array of validRoles
export type Roles = typeof validRoles[number];

/**
 * A context management for the user object. Instead of having to call a listener
 * inside each functional component, we can make use of React.Context to
 * define the UserProvider which make the user available through the useUser
 * useContext above in all components beneath it.
 * @param children
 * @constructor
 */
export function UserProvider({ children }: Children) {
  const [user, setUser] = useState<User | null>(null);
  const [isUserAdmin, setIsUserAdmin] = useState<boolean>(false);
  const [userRoles, setUserRoles] = useState<Roles[]>([]);
  useEffect(() => {
    if (user) {
      getDocument('users', user.uid)
        .then((userDoc) => {
          if (userDoc) setUserRoles(userDoc.association_permissions as unknown as Roles[]);
        })
        .catch((e: unknown) => {
          if (e instanceof Error) {
            console.error(`Unable to fetch user roles for user ${user.uid}: ${e.stack || 'Unknown error'}`);
          }
        });
      user.getIdTokenResult()
        .then((idTokenResult) => {
          setIsUserAdmin(idTokenResult.claims.admin as unknown as boolean);
        })
        .catch((e: unknown) => {
          if (e instanceof Error) {
            console.error(`Unable to fetch user ${e.stack || 'Unknown error'}`);
          }
        });
    }
  }, [user]);

  useEffect(() => {
    const res = isLoggedInObserver(setUser);
    return () => res();
  }, []);

  return (
    <UserContext.Provider value={user}>
      <AdminContext.Provider value={isUserAdmin}>
        <RolesContext.Provider value={userRoles}>
          {children}
        </RolesContext.Provider>
      </AdminContext.Provider>
    </UserContext.Provider>
  );
}

/**
 * Provided an uid, return the users' corresponding display name
 * @param uid The unique user id
 * @param userCollectionPath
 */
export const getUserNameFromId = async (uid:string, userCollectionPath = 'users') => {
  const userDocument = await getDocument(userCollectionPath, uid) as { displayName?: string };
  return userDocument?.displayName;
};

export const getUserNameFromIds = (uids: string[], userCollectionPath = 'usersPublic') => (
  Promise.all(uids.map((uid) => getUserNameFromId(uid, userCollectionPath)))
);
